import type { ForwardedRef, JSX } from 'react';

import { forwardRef } from '@change-corgi/core-react/core';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import type { ButtonMode, ButtonProps } from '@change-corgi/design-system-legacy/components/actions';
import type { ResponsiveValue } from '@change-corgi/design-system-legacy/theme';
import { normalizeResponsiveValue } from '@change-corgi/design-system-legacy/theme';

import type { Channel } from '../shared/types';

import { getSocialColor, getSocialIcon, isButtonFillable } from './shared/mapping';

type Props = Omit<ButtonProps, 'variant' | 'icon' | 'iconPosition' | 'mode'> & {
	channel: Channel;
	filled?: boolean;
	iconOnly?: ResponsiveValue<boolean>;
};

function SocialButtonInner(
	{ children, channel, filled, iconOnly, ...rest }: Props,
	ref: ForwardedRef<HTMLButtonElement>,
): JSX.Element {
	const iconOnlyResponsive = normalizeResponsiveValue(iconOnly);
	const mode = iconOnlyResponsive.map((value): ButtonMode | undefined | null => {
		if (value === true) return 'icon';
		if (value === false) return 'icon+label';
		return value;
	}) as ResponsiveValue<ButtonMode>;
	return (
		<Button
			variant={filled && isButtonFillable(channel) ? 'primary' : 'secondary'}
			icon={getSocialIcon(channel)}
			color={getSocialColor(channel)}
			mode={mode}
			{...rest}
			ref={ref}
		>
			{children}
		</Button>
	);
}

/**
 * @doc $DOC:SocialButton
 */
export const SocialButton = forwardRef(SocialButtonInner);
